import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { TrendingDown, AlertTriangle, Package, RefreshCw, Download, ShoppingCart, Search, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';

const InventoryForecastPage = () => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchForecast = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/inventory/forecast', { params: { days_ahead: 90 } });
      setForecast(response.data);
    } catch (error) {
      toast.error('Eroare la incarcare predictii');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const handleExport = () => {
    try {
      const exportData = (forecast?.forecasts || []).map(f => ({
        'Cod': f.article_code,
        'Articol': f.article_name,
        'Stoc Curent': f.current_stock,
        'Stoc Minim': f.min_stock,
        'UM': f.unit,
        'Consum Mediu Zilnic': f.avg_daily_consumption,
        'Zile pana la epuizare': f.days_until_runout || 'N/A',
        'Cantitate Sugerata Comanda': f.suggested_order_qty
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Predictii Stoc');
      XLSX.writeFile(wb, `predictie_stoc_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export generat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const filteredForecasts = (forecast?.forecasts || []).filter(f =>
    f.article_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.article_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Predictii Stoc - Inventory Forecasting</h1>
          <p className="text-slate-600 mt-1">Analiza predictiva consum si sugestii aprovizionare</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchForecast} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Reincarca
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Articole Urmarite</p>
                <p className="text-2xl font-bold">{forecast?.total_articles_tracked || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Critice (sub 7 zile)</p>
                <p className="text-2xl font-bold text-red-600">{forecast?.critical_items || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avertisment (7-30 zile)</p>
                <p className="text-2xl font-bold text-orange-600">{forecast?.warning_items || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Cauta articol dupa nume sau cod..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12"
        />
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          </div>
        ) : filteredForecasts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Nu exista date de predictie</p>
            </CardContent>
          </Card>
        ) : (
          filteredForecasts.map((item) => (
            <Card key={item.article_id} className={`${
              item.is_critical ? 'border-red-400 bg-red-50' :
              item.is_warning ? 'border-orange-400 bg-orange-50' :
              'border-slate-200'
            }`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      item.is_critical ? 'bg-red-100' :
                      item.is_warning ? 'bg-orange-100' :
                      'bg-green-100'
                    }`}>
                      <TrendingDown className={`w-6 h-6 ${
                        item.is_critical ? 'text-red-600' :
                        item.is_warning ? 'text-orange-600' :
                        'text-green-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">{item.article_name}</h3>
                        <Badge variant="outline" className="text-xs">{item.article_code}</Badge>
                        {item.is_critical && (
                          <Badge className="bg-red-600 text-white text-xs">URGENT</Badge>
                        )}
                        {item.is_warning && (
                          <Badge className="bg-orange-600 text-white text-xs">AVERTISMENT</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Stoc Curent</p>
                          <p className={`font-bold ${
                            item.current_stock === 0 ? 'text-red-600' :
                            item.current_stock < item.min_stock ? 'text-orange-600' :
                            'text-green-600'
                          }`}>
                            {item.current_stock} {item.unit}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Consum Zilnic</p>
                          <p className="font-semibold">{item.avg_daily_consumption} {item.unit}/zi</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Epuizare in</p>
                          <p className={`font-bold ${
                            !item.days_until_runout ? 'text-green-600' :
                            item.days_until_runout < 7 ? 'text-red-600' :
                            item.days_until_runout < 30 ? 'text-orange-600' :
                            'text-slate-700'
                          }`}>
                            {item.days_until_runout ? `${item.days_until_runout} zile` : 'Stabil'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Data Estimata</p>
                          <p className="font-medium">
                            {item.estimated_runout_date ? new Date(item.estimated_runout_date).toLocaleDateString('ro-RO') : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Sugestie Comanda</p>
                          <p className="font-bold text-blue-600">
                            {item.suggested_order_qty} {item.unit}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {item.is_critical && (
                    <Button size="sm" className="bg-red-600 hover:bg-red-700">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Comanda Urgenta
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default InventoryForecastPage;